import Link from '@material-ui/core/Link';
import { getColor } from 'common/constants/_colors';
import { DATE_FORMAT_WITH_FULL_DAY } from 'common/constants/constants';
import { momentDateValidator } from 'common/utils/time.utils';
import { navigateBookDemoClick } from 'common/utils/utils';
import Icon from 'components/base/Icon';
import moment from 'moment';
import { useState } from 'react';
import { DateRangePicker } from 'react-dates';
import styled from 'styled-components';
import Text from './Text';
import { Flex } from './layout-components';
import { lightColorPalette } from '@linearb/linui-common';

const defaultHeight = 1.6;

const StyledWrapper = styled.div`
    * {
      box-sizing: content-box;
    }
        ${({ highlight }) =>
          `border: 1px solid ${
            highlight
              ? lightColorPalette.lbOrange_100
              : lightColorPalette.lbBlue_20
          } !important`};
    border-radius: 4px;
    &:hover {
      ${({ highlight }) =>
        `border: 1px solid ${
          highlight
            ? lightColorPalette.lbOrangeDark
            : lightColorPalette.lbBrightBlue_100
        }; background: ${
          highlight
            ? lightColorPalette.lbOrange_5
            : lightColorPalette.lbPurple_10
        } !important;`}

      input {
        color: ${getColor('waikawaGray')} !important;
      }
      svg > path:nth-child(2) {
          stroke: ${getColor('dodgerBlue')} !important;
        }
      }
    }
    > div > div > div {
      border: 0 !important;
      border-radius: 4px !important;
      background: transparent !important;
      padding-left: 1rem !important;
      > div:nth-child(1),
      > div:nth-child(3),
      > div:nth-child(4) {
        background: transparent !important;
        ${({ height }) =>
          `height: ${height ? height : defaultHeight}rem !important`};
        min-height: ${defaultHeight}rem;
        padding: 0.5rem 0 0.5rem !important;
        > input {
          background: transparent !important;
          border-radius: 4px !important;
          border: 0 !important;
          padding: 0 !important;
          color: ${getColor('shark')} !important;
          font-size: 1.2rem !important;
          font-weight: 400 !important;
          height: 100%;
        }
        > svg {
          top: unset !important;
          bottom: -1px !important;
        }
      }
      > div:nth-child(2),
      > div:nth-child(3) {
        > svg[viewbox] {
            display: none;
          }
        }
      }


    input {
      font-family: "Outfit", "Oxygen",
      "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue",
      sans-serif;
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
    }

    > div > div > div > div:nth-child(2) {
      z-index: 9999 !important;
      ${({ height }) =>
        `top: ${
          parseFloat(
            height && height > defaultHeight ? height : defaultHeight
          ) + 1
        }rem !important`};
      margin-top: 1px;
      & > div {
        box-shadow: 0 5px 5px -3px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12) !important;
      }
    }

`;

const UpgradeNoteWrapper = styled(Flex)`
  background: ${getColor('zircon')};
  width: 100%;
  margin: 0 2rem 2rem 2rem;
`;
const UpgradeNote = ({ dataRetentionDays }) => {
  return (
    <Flex width='100%'>
      <UpgradeNoteWrapper padding='small' items='center'>
        <Icon name='Clock' color='dodgerBlue' />
        <Text pl='1rem' size='medium'>
          Your license includes the last {dataRetentionDays} days’ metrics.
          Upgrade your plan for more.&nbsp;
          <Link onClick={() => navigateBookDemoClick()}>Learn more</Link>
        </Text>
      </UpgradeNoteWrapper>
    </Flex>
  );
};

// eslint-disable-next-line no-unused-vars
const MaxLimitNote = ({ dataRetentionDays }) => {
  const maxRetentionDate = moment()
    .subtract(dataRetentionDays, 'day')
    .startOf('day');
  const maxRetentionDateValidated = momentDateValidator(maxRetentionDate);
  return (
    <Flex width='100%'>
      <UpgradeNoteWrapper padding='small' items='center'>
        <Icon name='Clock' color='dodgerBlue' />
        <Text pl='1rem' size='medium'>
          Dashboard metrics currently available since{' '}
          {maxRetentionDateValidated.format(DATE_FORMAT_WITH_FULL_DAY)}
        </Text>
      </UpgradeNoteWrapper>
    </Flex>
  );
};

const RangePicker = props => {
  const {
    name,
    dataRetentionDays, // retentionDays based on license.retentionDays OR dashboard maxRetentionDate (For example: TeamGoals dashboard has max of 01/01/2022)
    licenseRetention, // retentionDays by license - only Team license will have a value
    height = defaultHeight,
    highlight = false,
    ...rest
  } = props;
  const {
    startDate = moment(),
    endDate = moment(),
    focusedInput = null,
    onDatesChange
  } = rest;
  const [_focusedInput, setFocusedInput] = useState(focusedInput);
  const isInvalidDate = date =>
    moment(date).isBefore(
      moment().subtract(dataRetentionDays, 'day').startOf('day')
    );

  const isInvalidDates = (startDate, endDate) =>
    !endDate || !startDate || endDate.isBefore(startDate);

  const getCalendarInfo = (dataRetentionDays, licenseRetention) => {
    if (dataRetentionDays) {
      if (licenseRetention === dataRetentionDays) {
        return <UpgradeNote dataRetentionDays={licenseRetention} />;
      }
      //TODO check what to do with this, remove due to cs CS-5507
      //else return <MaxLimitNote dataRetentionDays={dataRetentionDays} />;
    } else {
      return null;
    }
  };

  return (
    <StyledWrapper height={parseFloat(height)} highlight={highlight}>
      <DateRangePicker
        {...rest}
        hideKeyboardShortcutsPanel
        isDayBlocked={dataRetentionDays ? isInvalidDate : () => false}
        renderCalendarInfo={() =>
          getCalendarInfo(dataRetentionDays, licenseRetention)
        }
        startDate={startDate} // momentPropTypes.momentObj or null,
        endDate={endDate} // momentPropTypes.momentObj or null,
        focusedInput={_focusedInput} // PropTypes.oneOf([START_DATE, END_DATE]) or null,
        onFocusChange={setFocusedInput} // PropTypes.func.isRequired,
        onDatesChange={({ startDate, endDate }) => {
          if (dataRetentionDays && isInvalidDate(startDate)) {
            return;
          }
          if (isInvalidDates(startDate, endDate)) {
            onDatesChange &&
              onDatesChange({
                startDate: startDate ?? moment(),
                endDate: startDate
              });
          }
          onDatesChange &&
            onDatesChange({ startDate, endDate: endDate ?? startDate });
        }}
        startDateId={`${name}StartDate`}
        endDateId={`${name}EndDate`}
        readOnly
      />
    </StyledWrapper>
  );
};

export default RangePicker;
